<script setup lang="ts">
import {
	fetchProductsCount,
	fetchTrendingProducts,
	useProductsCount,
	useProductsList,
} from '~/data/products';
import ProductList from '../plp/ProductList.vue';
import FilterBar from '../filter-bar/index.vue';
import { Skeleton } from '@laam/ui/base';
import SelectPeriod from './SelectPeriod.vue';

const routeName = 'new-arrivals';
const nodeIds = [1, 21, 41, 54];

const store = useGlobalStore();

const period = ref(store.addOns?.new_arrivals_products_period || '7d');

const filters = useFilterParams(routeName);

const queryParams = computed(() => {
	return {
		period: period.value,
		...filters.value,
	};
});

const count = useProductsCount({
	routeName,
	queryKey: ['new-arrivals-count', nodeIds, queryParams],
	queryFn: () => fetchProductsCount(queryParams.value),
});

const {
	data,
	isLoading,
	hasNextPage,
	fetchNextPage,
	isFetchingNextPage,
	error: productListError,
} = useProductsList({
	queryKey: ['new-arrivals-products', nodeIds, queryParams],
	queryFn: ({ pageParam: cursor }) => {
		return fetchTrendingProducts(cursor as string, nodeIds, queryParams.value);
	},
	routeName,
});

watch(isLoading, (isLoading) => {
	if (!isLoading && productListError.value) {
		throw createError({
			statusCode: 404,
			message: 'Failed to load products',
			fatal: true,
			stack: undefined,
		});
	}
});
</script>

<template>
	<div>
		<div class="flex flex-row items-start justify-between lg:px-0 px-3xl">
			<div class="flex flex-col justify-between">
				<h2
					ref="title"
					class="text-lg font-semibold lg:display-xs text-gray-900"
				>
					Newly Launched
				</h2>
				<p
					v-if="count !== undefined && count >= 0"
					class="line-clamp-1 lg:text-sm text-xs text-gray-600 capitalize"
				>
					{{ new Intl.NumberFormat().format(count!) + ' items' }}
				</p>
				<Skeleton v-else class="w-12xl h-4xl mt-xs bg-gray-100 rounded" />
			</div>
			<div class="w-fit">
				<SelectPeriod v-model:period="period" entity="product-list" />
			</div>
		</div>
		<FilterBar :node-ids="nodeIds" :static="true" class="static" />
		<ProductList
			:infinite-loader="true"
			:fetch-next-page="fetchNextPage"
			:is-loading="isLoading"
			:data="data"
			:has-next-page="hasNextPage"
			:is-fetching-next-page="isFetchingNextPage"
		/>
	</div>
</template>
